import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Grid, Typography, makeStyles } from "@material-ui/core";
import { Formik } from "formik";
import type { FC } from "react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams, useParams } from "react-router-dom";
import * as Yup from "yup";

import { useCurrentPlatform } from "@/contexts";
import { useGetSportsLazyQuery } from "@/graphql";
import { useScopedNavigate } from "@/hooks";
import { useT } from "@/i18n";
import CustomInput from "src/components/customComponents/CustomInput";
import CustomSelectInput from "src/components/customComponents/CustomSelectInput";
import SubmitBanner from "src/components/dashboard/submitBanner";
import { CREATE_DISCIPLINE, UPDATE_DISCIPLINE } from "src/graphql/mutations";
import { DICIPLINE_BY_ID, DISCIPLINES } from "src/graphql/queries";
import { Compression } from "src/utils/helper-functions";
import { handlePreview } from "src/utils/imagePreview";

import UploadFiles from "../uploadFile";

const resultType = [
  {
    title: "Custom metrics",
  },
  {
    title: "Location & Weight fixed",
  },
];
const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: "32px",
    maxWidth: "1028px",
    border: "1px solid #EDF1F6",
    borderRadius: "8px",
    marginBottom: "40px",
    padding: theme.spacing(4),
  },
  subHeading: {
    fontWeight: 500,
  },
  mainHeading: {
    color: "#0A2540",
    fontSize: "18px",
    fontWeight: 700,
  },
  textfield: {
    background: "#FBFBFB",
    width: "100%",
    marginTop: 8,
  },
  button: {
    background: "linear-gradient(100deg, #FF5733 0.25%, #FD4C26 46.18%, #FA8D76 97.15%)",
  },
}));

const DisciplineForm: FC<any> = (props) => {
  const classes = useStyles();
  const t = useT();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const section = searchParams.get("section");
  const currentPlatform = useCurrentPlatform();
  const [getDiscipline] = useLazyQuery(DICIPLINE_BY_ID);
  const navigate = useScopedNavigate();
  const [isFound] = useState(id ? true : false);

  const [logo, setLogo] = useState<any[]>([]);
  const [data, setData] = useState<any>("");
  const [sportType, setSportType] = useState<any>();
  const [selectedSport, setSelectedSport] = useState<any>("");

  const [update, { loading: loader }] = useMutation(UPDATE_DISCIPLINE, {
    refetchQueries: [
      {
        query: DISCIPLINES,
      },
    ],
    onCompleted: () => {
      if (section === "superData") {
        navigate("/disciplines");
      } else {
        navigate(
          `/platforms/${currentPlatform.id}?platformActiveTab=disciplines&platformTab=settings`
        );
      }
    },
  });

  const [createDiscipline, { loading }] = useMutation(CREATE_DISCIPLINE, {
    refetchQueries: [
      {
        query: DISCIPLINES,
      },
    ],
    onCompleted: () => {
      navigate(
        `/platforms/${currentPlatform.id}?platformActiveTab=disciplines&platformTab=settings`
      );
    },
  });
  const [SportsFunction, { data: Sports }] = useGetSportsLazyQuery();

  useEffect(() => {
    SportsFunction({
      variables: {
        // filter: {
        //   enableDiscipline: true,
        // },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const DisciplineById = async (id: string) => {
    const variables = {
      id: parseInt(id),
    };
    try {
      const { data } = await getDiscipline({ variables });
      setData(data?.discipline);
      setSelectedSport(data?.discipline?.sport?.id);
      await handlePreview(data?.discipline?.icon, setLogo);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (id) {
      DisciplineById(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (Sports?.sports && Sports?.sports?.length > 0 && selectedSport) {
      setSportType(Sports?.sports?.find((item) => item?.id === parseInt(selectedSport)));
    }
  }, [Sports, selectedSport]);
  const handleSetImage = (file, setFunction, avatar) => {
    Compression(file[0], 1)
      .then((compressedBlob) => {
        const compressedFile = new File([compressedBlob], file[0].name, {
          type: compressedBlob.type,
        });
        setFunction(avatar, [compressedFile]);
        // setFunction([compressedFile]);
      })
      .catch((error) => {
        console.error("Error while compressing the file:", error);
      });
  };
  return (
    <Formik
      enableReinitialize={id ? true : false}
      initialValues={{
        title: data?.title ?? "",
        resultType: data?.resultType ?? "",
        icon: logo ?? [],
        sport: data?.sport?.id ?? "",
      }}
      validationSchema={Yup.object().shape({
        title: Yup.string().required(t("title is required")),
      })}
      onSubmit={async (values): Promise<void> => {
        if (id) {
          const variables = {
            ...values,
            id: parseInt(id),
            icon: values?.icon[0],
            sport: parseInt(values?.sport),
          };
          try {
            await update({ variables });
            toast.success(t("Discipline Updated"));
          } catch (error) {
            toast.error(error.message);
          }
        } else {
          const variables = {
            ...values,
            icon: values?.icon[0],
            sport: parseInt(values?.sport),
            platform: currentPlatform.id,
          };
          try {
            await createDiscipline({ variables });
            toast.success(t("Discipline Created"));
          } catch (error) {
            toast.error(error.message);
          }
        }
      }}
    >
      {({ errors, handleChange, handleSubmit, setFieldValue, touched, values }): JSX.Element => (
        <form onSubmit={handleSubmit} {...props}>
          <SubmitBanner
            title={t("Add New Discipline")}
            id={id}
            path={
              section === "superData"
                ? "/disciplines"
                : `/platforms/${currentPlatform.id}?platformActiveTab=disciplines&platformTab=settings`
            }
            SecText={t("Edit Discipline")}
            isFound={isFound}
            loading={loading}
            loader={loader}
            // handleAdd={handleAddMember}
          />

          <Grid container xs={12} md={12} lg={12}>
            <Grid item md={8} xs={12}>
              <Grid item container xs={12} spacing={3}>
                <Grid item md={6} xs={12}>
                  <CustomInput
                    handleChange={handleChange}
                    helperText={touched.title && errors.title}
                    error={Boolean(touched.title && errors.title)}
                    value={values.title}
                    placeholder={t("Enter Title")}
                    isRequired={true}
                    label={t("Discipline title")}
                    name="title"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Box>
                    <CustomSelectInput
                      handleChange={(event) => {
                        handleChange(event);
                        setSelectedSport(event.target.value);
                      }}
                      value={values.sport}
                      placeholder={t("Sport")}
                      isRequired={true}
                      label={t("Select sport")}
                      name="sport"
                      options={
                        Sports?.sports && Sports?.sports?.length > 0
                          ? Sports?.sports?.map((item) => {
                              return {
                                value: item?.id,
                                title: item?.title ?? "",
                              };
                            })
                          : []
                      }
                      firstOption={true}
                    />
                  </Box>
                </Grid>

                {values?.sport && sportType?.displayListResults ? (
                  <Grid item xs={12}>
                    <Box>
                      <CustomSelectInput
                        handleChange={handleChange}
                        value={values.resultType}
                        placeholder={t("Results type")}
                        isRequired={true}
                        label={t("Results type")}
                        name="resultType"
                        options={
                          resultType.length > 0
                            ? resultType?.map((item) => {
                                return {
                                  value: item?.title,
                                  title: item?.title,
                                };
                              })
                            : []
                        }
                      />
                    </Box>
                  </Grid>
                ) : (
                  ""
                )}
              </Grid>

              <Typography className={classes.subHeading} sx={{ mt: 2, mb: 1 }} variant="body1">
                {t("Icon")}
              </Typography>
              <UploadFiles
                maxFiles={1}
                accept="image/*"
                files={values?.icon}
                onDrop={(file) => handleSetImage(file, setFieldValue, "icon")}
                onRemove={() => setFieldValue("icon", [])}
                onRemoveAll={() => setFieldValue("icon", [])}
              />
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

export default DisciplineForm;
